import { useState, useMemo } from "react";
import { useAuth } from "@/contexts/AuthContext";
import { useNavigate } from "react-router-dom";
import { Loader2, ArrowLeft, ArrowRight, CheckCircle2 } from "lucide-react";
import axios from "axios";
import { formatUSPhone } from "@/lib/formatters";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// ─── INLINE QUIZ DATA ───────────────────────────────────────
const quizSteps = [
  { id: "goal", question: "What is your main goal?", options: [
    { value: "goal_weightloss", label: "Lose stubborn body fat" },
    { value: "goal_energy", label: "Improve energy & recovery" },
    { value: "goal_antiaging", label: "Anti-aging & longevity" },
    { value: "goal_muscle", label: "Build lean muscle" },
    { value: "goal_health", label: "Improve overall health" },
  ]},
  { id: "frustration", question: "Biggest challenge?", options: [
    { value: "cant_lose_weight", label: "Can't lose weight no matter what" },
    { value: "low_energy", label: "Tired / low energy" },
    { value: "no_results", label: "Not seeing results from workouts" },
    { value: "aging_fast", label: "Aging faster than I should" },
    { value: "plateaued", label: "I've plateaued" },
  ]},
  { id: "age_range", question: "Age range?", options: [
    { value: "under_30", label: "Under 30" },
    { value: "30_39", label: "30–39" },
    { value: "40_49", label: "40–49" },
    { value: "50_59", label: "50–59" },
    { value: "60_plus", label: "60+" },
  ]},
  { id: "condition", question: "Current shape/health?", options: [
    { value: "overweight", label: "Overweight" },
    { value: "slightly_overweight", label: "Slightly overweight" },
    { value: "average", label: "Average" },
    { value: "fit_optimize", label: "Fit but want optimization" },
    { value: "athletic", label: "Athletic" },
  ]},
  { id: "experience", question: "Tried anything before?", options: [
    { value: "diet_exercise", label: "Diet & exercise" },
    { value: "supplements", label: "Supplements" },
    { value: "hormone_therapy", label: "Hormone therapy (TRT/HRT)" },
    { value: "peptides_before", label: "Peptides before" },
    { value: "nothing_yet", label: "Nothing yet" },
  ]},
  { id: "timeframe", question: "How soon do you want results?", options: [
    { value: "asap", label: "ASAP" },
    { value: "1_3_months", label: "Within 1–3 months" },
    { value: "3_6_months", label: "3–6 months" },
    { value: "exploring", label: "Just exploring" },
  ]},
  { id: "commitment", question: "How serious are you?", options: [
    { value: "very_serious", label: "Very serious — ready to start" },
    { value: "serious_learn", label: "Serious but want to learn more" },
    { value: "researching", label: "Just researching" },
  ]},
  { id: "medical", question: "Open to a doctor-guided program?", options: [
    { value: "yes", label: "Yes" },
    { value: "maybe", label: "Maybe" },
    { value: "not_sure", label: "Not sure" },
  ]},
  { id: "budget", question: "Programs range $200–$800+/mo. Comfortable?", options: [
    { value: "yes", label: "Yes" },
    { value: "maybe", label: "Maybe" },
    { value: "no", label: "No" },
  ]},
];

const peptideMap = {
  goal_weightloss: ["Semaglutide", "Tirzepatide"],
  goal_energy: ["CJC-1295 / Ipamorelin", "BPC-157"],
  goal_antiaging: ["GHK-Cu", "Epithalon"],
  goal_muscle: ["CJC-1295 / Ipamorelin", "TB-500"],
  goal_health: ["Thymosin Alpha-1", "BPC-157"],
};

function getRecommended(answers) {
  const goals = answers.goal || [];
  const recs = new Set();
  goals.forEach((g) => { (peptideMap[g] || []).forEach((p) => recs.add(p)); });
  if (recs.size < 2) { recs.add("BPC-157"); recs.add("CJC-1295 / Ipamorelin"); }
  return Array.from(recs).slice(0, 4);
}

// Parse any error shape into a readable string.
function parseError(err, fallback = "Something went wrong. Please try again.") {
  if (!err) return fallback;
  const detail = err?.response?.data?.detail;
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail) && detail[0]?.msg) return detail[0].msg;
  if (err?.response?.status === 401) return "Invalid email or password";
  if (err?.response?.status === 403) return "Your account is not active. Please contact support.";
  if (err?.response?.status >= 500) return "Server error. Please try again in a moment.";
  if (err?.message?.includes("Network")) return "Can't reach the server. Check your connection.";
  return fallback;
}

// ─── MAIN COMPONENT ─────────────────────────────────────────
export default function AdminLogin() {
  const { login, checkAuth } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  // Signup / quiz state
  const [showSignup, setShowSignup] = useState(false);
  const [signupForm, setSignupForm] = useState({ first_name: "", last_name: "", email: "", phone: "", password: "" });
  const [signupLoading, setSignupLoading] = useState(false);

  // Forgot password state
  const [showForgot, setShowForgot] = useState(false);
  const [forgotEmail, setForgotEmail] = useState("");
  const [forgotCode, setForgotCode] = useState("");
  const [forgotCodeSent, setForgotCodeSent] = useState(false);
  const [newPassword, setNewPassword] = useState("");
  const [forgotLoading, setForgotLoading] = useState(false);
  const [resetDone, setResetDone] = useState(false);

  // Patient setup (after quiz submission detected existing applicant)
  const [showSetup, setShowSetup] = useState(false);
  const [setupEmail, setSetupEmail] = useState("");
  const [setupPassword, setSetupPassword] = useState("");
  const [setupFirst, setSetupFirst] = useState("");
  const [setupLast, setSetupLast] = useState("");
  const [setupLoading, setSetupLoading] = useState(false);
  const [setupError, setSetupError] = useState("");

  // Inline quiz state
  const [showQuiz, setShowQuiz] = useState(false);
  const [quizStep, setQuizStep] = useState(0);
  const [quizAnswers, setQuizAnswers] = useState({});
  const [quizContact, setQuizContact] = useState({ first_name: "", last_name: "", email: "", phone: "", password: "" });
  const [quizPhase, setQuizPhase] = useState("questions");
  const [quizLoading, setQuizLoading] = useState(false);

  const recommendedPeptides = useMemo(() => getRecommended(quizAnswers), [quizAnswers]);

  const resetAll = () => {
    setShowSetup(false); setShowQuiz(false); setShowSignup(false); setShowForgot(false);
    setError(""); setQuizStep(0); setQuizAnswers({}); setQuizPhase("questions");
    setForgotCodeSent(false); setForgotCode(""); setForgotCodeHint(""); setNewPassword(""); setResetDone(false);
  };

  // ─── UNIFIED LOGIN (all roles) ──────────────────────────
  const [require2FA, setRequire2FA] = useState(false);
  const [requireTOTP, setRequireTOTP] = useState(false);
  const [pendingUserId, setPendingUserId] = useState("");
  const [maskedPhone, setMaskedPhone] = useState("");
  const [twoFACode, setTwoFACode] = useState("");
  const [totpCode, setTotpCode] = useState("");
  const [twoFALoading, setTwoFALoading] = useState(false);
  const [resendCooldown, setResendCooldown] = useState(0);

  const handleLogin = async (e) => {
    e.preventDefault();
    if (!email.trim() || !password) { setError("Enter email and password"); return; }
    setError(""); setLoading(true);
    try {
      // First step: email + password via raw axios so we can detect 2FA response
      const { data } = await axios.post(`${API}/auth/login`, { email: email.trim(), password }, { withCredentials: true });
      if (data.requires_totp) {
        setRequireTOTP(true);
        setPendingUserId(data.pending_user_id);
        return;
      }
      if (data.requires_2fa) {
        setRequire2FA(true);
        setPendingUserId(data.pending_user_id);
        setMaskedPhone(data.masked_phone || "");
        return;
      }
      // No 2FA (patient) — hydrate auth context
      if (data.access_token) localStorage.setItem("pr_access_token", data.access_token);
      await checkAuth();
      navigate(data.role === "patient" ? "/portal" : "/admin");
    } catch (err) {
      setError(parseError(err, "Login failed. Please check your credentials."));
    } finally { setLoading(false); }
  };

  const handleVerifyTOTP = async () => {
    const code = totpCode.trim().toUpperCase();
    if (!code) { setError("Enter the code from your authenticator or a backup code"); return; }
    setError(""); setTwoFALoading(true);
    try {
      const { data } = await axios.post(`${API}/auth/verify-totp`, {
        pending_user_id: pendingUserId, code,
      }, { withCredentials: true });
      if (data.access_token) localStorage.setItem("pr_access_token", data.access_token);
      await checkAuth();
      navigate(data.role === "patient" ? "/portal" : "/admin");
    } catch (err) {
      setError(parseError(err, "Invalid code"));
    } finally { setTwoFALoading(false); }
  };

  const cancelTOTP = () => {
    setRequireTOTP(false); setPendingUserId(""); setTotpCode(""); setError("");
  };

  const handleVerify2FA = async () => {
    if (!twoFACode.trim()) { setError("Enter the code from your phone"); return; }
    setError(""); setTwoFALoading(true);
    try {
      const { data } = await axios.post(`${API}/auth/verify-2fa`, {
        pending_user_id: pendingUserId, code: twoFACode.trim(),
      }, { withCredentials: true });
      if (data.access_token) localStorage.setItem("pr_access_token", data.access_token);
      await checkAuth();
      navigate(data.role === "patient" ? "/portal" : "/admin");
    } catch (err) {
      setError(parseError(err, "Invalid code"));
    } finally { setTwoFALoading(false); }
  };

  const handleResend2FA = async () => {
    if (resendCooldown > 0) return;
    setError(""); setTwoFALoading(true);
    try {
      const { data } = await axios.post(`${API}/auth/resend-2fa`, { pending_user_id: pendingUserId });
      setMaskedPhone(data.masked_phone || maskedPhone);
      setResendCooldown(30);
      const t = setInterval(() => setResendCooldown((c) => { if (c <= 1) { clearInterval(t); return 0; } return c - 1; }), 1000);
    } catch (err) {
      setError(parseError(err, "Couldn't resend code"));
    } finally { setTwoFALoading(false); }
  };

  const cancelTwoFA = () => {
    setRequire2FA(false); setPendingUserId(""); setMaskedPhone(""); setTwoFACode(""); setError("");
  };

  const handleDirectSignup = async (e) => {
    e.preventDefault();
    if (!signupForm.first_name || !signupForm.email || !signupForm.phone || !signupForm.password) {
      setError("Name, email, phone, and password required"); return;
    }
    setError(""); setSignupLoading(true);
    try {
      await axios.post(`${API}/auth/patient-register`, {
        email: signupForm.email, password: signupForm.password,
        first_name: signupForm.first_name, last_name: signupForm.last_name, phone: signupForm.phone,
      }, { withCredentials: true });
      await login(signupForm.email, signupForm.password);
      navigate("/portal");
    } catch (err) {
      const detail = err.response?.data?.detail;
      if (detail === "Account already exists. Please log in.") {
        try {
          await login(signupForm.email, signupForm.password);
          navigate("/portal");
          return;
        } catch { setError("Account exists. Please log in with your password."); setShowSignup(false); setEmail(signupForm.email); }
      } else {
        setError(parseError(err));
      }
    } finally { setSignupLoading(false); }
  };

  const handleSetupAccount = async (e) => {
    e.preventDefault();
    setSetupError("");
    if (!setupFirst || !setupPassword) { setSetupError("Please fill in all fields"); return; }
    if (setupPassword.length < 6) { setSetupError("Password must be at least 6 characters"); return; }
    setSetupLoading(true);
    try {
      await axios.post(`${API}/auth/patient-register`, {
        email: setupEmail, password: setupPassword, first_name: setupFirst, last_name: setupLast,
      }, { withCredentials: true });
      await login(setupEmail, setupPassword);
      navigate("/portal");
    } catch (err) {
      const detail = err.response?.data?.detail;
      if (detail === "Account already exists. Please log in.") {
        try { await login(setupEmail, setupPassword); navigate("/portal"); return; }
        catch { setSetupError("Account exists. Please go back and log in with your password."); }
      } else {
        setSetupError(parseError(err, "Setup failed"));
      }
    } finally { setSetupLoading(false); }
  };

  // Quiz handlers
  const toggleQuizAnswer = (value) => {
    const step = quizSteps[quizStep];
    const curr = quizAnswers[step.id] || [];
    setQuizAnswers({ ...quizAnswers, [step.id]: curr.includes(value) ? curr.filter((v) => v !== value) : [...curr, value] });
  };
  const quizCanProceed = () => {
    if (quizPhase === "contact") return quizContact.first_name && quizContact.last_name && quizContact.email && quizContact.phone && quizContact.password && quizContact.password.length >= 6;
    const step = quizSteps[quizStep];
    return (quizAnswers[step.id] || []).length > 0;
  };
  const quizNext = () => {
    if (quizStep < quizSteps.length - 1) setQuizStep(quizStep + 1);
    else setQuizPhase("contact");
  };
  const submitQuizAndRegister = async () => {
    setQuizLoading(true); setSetupError("");
    try {
      await axios.post(`${API}/quiz`, {
        answers: quizAnswers,
        contact: { first_name: quizContact.first_name, last_name: quizContact.last_name, email: quizContact.email, phone: quizContact.phone },
        recommended_peptides: recommendedPeptides,
      });
      await axios.post(`${API}/auth/patient-register`, {
        email: quizContact.email, password: quizContact.password,
        first_name: quizContact.first_name, last_name: quizContact.last_name,
      }, { withCredentials: true });
      await login(quizContact.email, quizContact.password);
      navigate("/portal");
    } catch (err) {
      const detail = err.response?.data?.detail;
      if (detail === "Account already exists. Please log in.") {
        try { await login(quizContact.email, quizContact.password); navigate("/portal"); return; }
        catch {
          setSetupError("Account created! Please log in with your email and password.");
          setShowQuiz(false); setEmail(quizContact.email);
        }
      } else {
        setSetupError(parseError(err));
      }
      setQuizPhase("contact");
    } finally { setQuizLoading(false); }
  };

  const handleForgotSendCode = async () => {
    if (!forgotEmail.trim()) { setError("Enter your email"); return; }
    setError(""); setForgotLoading(true);
    try {
      const { data } = await axios.post(`${API}/auth/forgot-password`, { email: forgotEmail.trim() });
      setForgotCodeSent(true);
    } catch (err) {
      setError(parseError(err, "Failed to send reset code"));
    } finally { setForgotLoading(false); }
  };
  const handleResetPassword = async () => {
    if (!forgotCode.trim() || !newPassword) { setError("Enter the code and new password"); return; }
    setError(""); setForgotLoading(true);
    try {
      await axios.post(`${API}/auth/reset-password`, { email: forgotEmail.trim(), code: forgotCode.trim(), new_password: newPassword });
      setResetDone(true);
    } catch (err) {
      setError(parseError(err, "Failed to reset password"));
    } finally { setForgotLoading(false); }
  };

  const quizProgress = quizPhase === "contact" ? 100 : ((quizStep + 1) / quizSteps.length) * 100;

  // ─── RENDER ───────────────────────────────────────────────
  return (
    <div className="min-h-screen bg-[#2A3324] flex items-center justify-center px-6 py-10">
      <div className="w-full max-w-md">
        <div className="text-center mb-8">
          <button onClick={() => navigate("/")} data-testid="back-to-site" className="inline-flex items-center gap-2 text-[#F7F5F0]/40 hover:text-[#F7F5F0]/70 text-xs font-sans mb-4 transition-colors">
            <ArrowLeft size={14} /> Back to website
          </button>
          <h1 className="font-serif text-3xl font-light text-[#F7F5F0] tracking-tight">
            peptides<span className="font-semibold">recovery</span>
          </h1>
        </div>

        {/* ─── UNIFIED LOGIN ─── */}
        {!showSetup && !showQuiz && !showSignup && !showForgot && !require2FA && !requireTOTP && (
          <form onSubmit={handleLogin} data-testid="login-form" className="bg-[#F7F5F0] rounded-2xl p-8 shadow-2xl">
            <h2 className="text-2xl font-serif font-normal text-[#2A3324] mb-1">Log In</h2>
            <p className="text-sm font-sans text-[#2A3324]/40 mb-5">Access your account</p>

            {error && <div data-testid="login-error" className="bg-red-50 text-red-600 text-sm p-3 rounded-lg mb-4">{error}</div>}

            <div className="space-y-4">
              <div>
                <label className="text-xs font-sans font-medium uppercase tracking-wider text-[#2A3324]/50 mb-1 block">Email</label>
                <input data-testid="login-email" type="email" value={email} onChange={(e) => setEmail(e.target.value)} autoComplete="username"
                  className="w-full px-4 py-3 rounded-xl border border-[#2A3324]/10 bg-white text-[#2A3324] text-sm focus:outline-none focus:ring-2 focus:ring-[#5C715E]/30" placeholder="your@email.com" required />
              </div>
              <div>
                <label className="text-xs font-sans font-medium uppercase tracking-wider text-[#2A3324]/50 mb-1 block">Password</label>
                <input data-testid="login-password" type="password" value={password} onChange={(e) => setPassword(e.target.value)} autoComplete="current-password"
                  className="w-full px-4 py-3 rounded-xl border border-[#2A3324]/10 bg-white text-[#2A3324] text-sm focus:outline-none focus:ring-2 focus:ring-[#5C715E]/30" placeholder="Enter password" required />
              </div>
              <button data-testid="login-submit-btn" type="submit" disabled={loading}
                className="w-full bg-[#C47C5D] text-[#F7F5F0] rounded-xl py-3 text-sm font-sans font-medium hover:bg-[#B36B4C] transition-all disabled:opacity-50 flex items-center justify-center gap-2">
                {loading && <Loader2 size={16} className="animate-spin" />}
                {loading ? "Logging in..." : "Log In"}
              </button>
              <button type="button" data-testid="forgot-password-link" onClick={() => { setShowForgot(true); setForgotEmail(email); setError(""); }}
                className="w-full text-xs font-sans text-[#2A3324]/40 hover:text-[#5C715E] transition-colors py-1 text-center">
                Forgot password?
              </button>
            </div>

            <div className="mt-5 pt-5 border-t border-[#2A3324]/10 text-center">
              <p className="text-xs font-sans text-[#2A3324]/40 mb-3">Don't have an account?</p>
              <button type="button" data-testid="create-account-btn"
                onClick={() => { setShowSignup(true); setShowQuiz(false); setError(""); }}
                className="w-full bg-[#2A3324] text-[#F7F5F0] rounded-xl py-3 text-sm font-sans font-medium hover:bg-[#3a4534] transition-all flex items-center justify-center gap-2 mb-2">
                Create Account <ArrowRight size={14} />
              </button>
              <button type="button" data-testid="take-quiz-btn"
                onClick={() => { setShowQuiz(true); setShowSignup(false); setQuizStep(0); setQuizPhase("questions"); setError(""); }}
                className="w-full text-xs font-sans text-[#2A3324]/40 hover:text-[#5C715E] transition-colors py-2">
                Or take the assessment quiz first
              </button>
            </div>
          </form>
        )}

        {/* ─── TOTP VERIFICATION (authenticator app) ─── */}
        {requireTOTP && (
          <div data-testid="totp-form" className="bg-[#F7F5F0] rounded-2xl p-8 shadow-2xl">
            <button type="button" onClick={cancelTOTP} className="text-xs font-sans text-[#2A3324]/40 hover:text-[#2A3324] mb-4 flex items-center gap-1">
              <ArrowLeft size={12} /> Back to login
            </button>
            <h2 className="text-2xl font-serif font-normal text-[#2A3324] mb-1">Authenticator Code</h2>
            <p className="text-sm font-sans text-[#2A3324]/60 mb-5">
              Open Google Authenticator, Authy, or 1Password and enter the current 6-digit code.
              Lost your phone? Enter a backup code instead.
            </p>
            {error && <div data-testid="totp-error" className="bg-red-50 text-red-600 text-sm p-3 rounded-lg mb-4">{error}</div>}
            <div className="space-y-4">
              <div>
                <label className="text-xs font-sans font-medium uppercase tracking-wider text-[#2A3324]/50 mb-1 block">Code</label>
                <input data-testid="totp-code-input"
                  autoFocus
                  inputMode="text"
                  value={totpCode}
                  onChange={(e) => setTotpCode(e.target.value.toUpperCase().slice(0, 9))}
                  onKeyDown={(e) => { if (e.key === "Enter") handleVerifyTOTP(); }}
                  className="w-full px-4 py-3 rounded-xl border border-[#2A3324]/10 bg-white text-[#2A3324] text-2xl font-mono tracking-[0.4em] text-center focus:outline-none focus:ring-2 focus:ring-[#5C715E]/30"
                  placeholder="000000"
                />
              </div>
              <button data-testid="totp-submit" onClick={handleVerifyTOTP} disabled={twoFALoading || !totpCode}
                className="w-full bg-[#C47C5D] text-[#F7F5F0] rounded-xl py-3 text-sm font-sans font-medium hover:bg-[#B36B4C] transition-all disabled:opacity-50 flex items-center justify-center gap-2">
                {twoFALoading && <Loader2 size={16} className="animate-spin" />}
                {twoFALoading ? "Verifying..." : "Verify & Sign In"}
              </button>
            </div>
          </div>
        )}

        {/* ─── 2FA SMS VERIFICATION ─── */}
        {require2FA && (
          <div data-testid="twofa-form" className="bg-[#F7F5F0] rounded-2xl p-8 shadow-2xl">
            <button type="button" onClick={cancelTwoFA} className="text-xs font-sans text-[#2A3324]/40 hover:text-[#2A3324] mb-4 flex items-center gap-1">
              <ArrowLeft size={12} /> Back to login
            </button>
            <h2 className="text-2xl font-serif font-normal text-[#2A3324] mb-1">Verify It's You</h2>
            <p className="text-sm font-sans text-[#2A3324]/60 mb-5">
              We sent a 6-digit code to <span className="font-medium text-[#2A3324]">{maskedPhone}</span>.
            </p>
            {error && <div data-testid="twofa-error" className="bg-red-50 text-red-600 text-sm p-3 rounded-lg mb-4">{error}</div>}
            <div className="space-y-4">
              <div>
                <label className="text-xs font-sans font-medium uppercase tracking-wider text-[#2A3324]/50 mb-1 block">Verification Code</label>
                <input data-testid="twofa-code" type="text" inputMode="numeric" maxLength={6}
                  value={twoFACode}
                  onChange={(e) => setTwoFACode(e.target.value.replace(/\D/g, ""))}
                  onKeyDown={(e) => { if (e.key === "Enter") handleVerify2FA(); }}
                  autoFocus
                  className="w-full px-4 py-3 rounded-xl border border-[#2A3324]/10 bg-white text-[#2A3324] text-2xl font-mono tracking-[0.4em] text-center focus:outline-none focus:ring-2 focus:ring-[#5C715E]/30"
                  placeholder="000000"
                />
              </div>
              <button data-testid="twofa-submit" onClick={handleVerify2FA} disabled={twoFALoading || twoFACode.length < 6}
                className="w-full bg-[#C47C5D] text-[#F7F5F0] rounded-xl py-3 text-sm font-sans font-medium hover:bg-[#B36B4C] transition-all disabled:opacity-50 flex items-center justify-center gap-2">
                {twoFALoading && <Loader2 size={16} className="animate-spin" />}
                {twoFALoading ? "Verifying..." : "Verify & Sign In"}
              </button>
              <button type="button" data-testid="twofa-resend" onClick={handleResend2FA} disabled={resendCooldown > 0 || twoFALoading}
                className="w-full text-xs font-sans text-[#2A3324]/50 hover:text-[#5C715E] disabled:opacity-40 disabled:cursor-not-allowed transition-colors py-2 text-center">
                {resendCooldown > 0 ? `Resend code in ${resendCooldown}s` : "Didn't get it? Resend code"}
              </button>
            </div>
          </div>
        )}

        {/* ─── DIRECT SIGNUP ─── */}
        {showSignup && !showSetup && !showQuiz && (
          <form onSubmit={handleDirectSignup} data-testid="patient-signup-form" className="bg-[#F7F5F0] rounded-2xl p-8 shadow-2xl">
            <button type="button" onClick={resetAll} className="text-xs font-sans text-[#2A3324]/40 hover:text-[#2A3324] mb-4 flex items-center gap-1">
              <ArrowLeft size={12} /> Back to login
            </button>
            <h2 className="text-2xl font-serif font-normal text-[#2A3324] mb-1">Create Patient Account</h2>
            <p className="text-sm font-sans text-[#2A3324]/40 mb-6">Sign up to access your patient portal</p>
            {error && <div data-testid="signup-error" className="bg-red-50 text-red-600 text-sm p-3 rounded-lg mb-4">{error}</div>}
            <div className="space-y-3">
              <div className="grid grid-cols-2 gap-3">
                <input data-testid="signup-first" type="text" placeholder="First Name *" value={signupForm.first_name} onChange={(e) => setSignupForm({...signupForm, first_name: e.target.value})}
                  className="px-4 py-3 rounded-xl border border-[#2A3324]/10 bg-white text-[#2A3324] text-sm focus:outline-none focus:ring-2 focus:ring-[#5C715E]/30" required />
                <input data-testid="signup-last" type="text" placeholder="Last Name" value={signupForm.last_name} onChange={(e) => setSignupForm({...signupForm, last_name: e.target.value})}
                  className="px-4 py-3 rounded-xl border border-[#2A3324]/10 bg-white text-[#2A3324] text-sm focus:outline-none focus:ring-2 focus:ring-[#5C715E]/30" />
              </div>
              <input data-testid="signup-email" type="email" placeholder="Email *" value={signupForm.email} onChange={(e) => setSignupForm({...signupForm, email: e.target.value})}
                className="w-full px-4 py-3 rounded-xl border border-[#2A3324]/10 bg-white text-[#2A3324] text-sm focus:outline-none focus:ring-2 focus:ring-[#5C715E]/30" required />
              <input data-testid="signup-phone" type="tel" placeholder="Phone *" value={signupForm.phone} onChange={(e) => setSignupForm({...signupForm, phone: formatUSPhone(e.target.value)})}
                className="w-full px-4 py-3 rounded-xl border border-[#2A3324]/10 bg-white text-[#2A3324] text-sm focus:outline-none focus:ring-2 focus:ring-[#5C715E]/30" required />
              <input data-testid="signup-password" type="password" placeholder="Password *" value={signupForm.password} onChange={(e) => setSignupForm({...signupForm, password: e.target.value})}
                className="w-full px-4 py-3 rounded-xl border border-[#2A3324]/10 bg-white text-[#2A3324] text-sm focus:outline-none focus:ring-2 focus:ring-[#5C715E]/30" required />
              <button data-testid="signup-submit" type="submit" disabled={signupLoading}
                className="w-full bg-[#5C715E] text-[#F7F5F0] rounded-xl py-3 text-sm font-sans font-medium hover:bg-[#4A5A4B] transition-all disabled:opacity-50 flex items-center justify-center gap-2">
                {signupLoading && <Loader2 size={16} className="animate-spin" />}
                {signupLoading ? "Creating..." : "Create Account"}
              </button>
            </div>
            <p className="text-[10px] font-sans text-[#2A3324]/30 mt-4 text-center">
              Want a personalized protocol recommendation? <button type="button" onClick={() => { setShowSignup(false); setShowQuiz(true); setQuizStep(0); setQuizPhase("questions"); }} className="text-[#5C715E] font-medium hover:underline">Take the quiz</button>
            </p>
          </form>
        )}

        {/* ─── FORGOT PASSWORD ─── */}
        {showForgot && !showSetup && !showQuiz && !showSignup && (
          <div data-testid="forgot-password-form" className="bg-[#F7F5F0] rounded-2xl p-8 shadow-2xl">
            <button type="button" onClick={resetAll} className="text-xs font-sans text-[#2A3324]/40 hover:text-[#2A3324] mb-4 flex items-center gap-1">
              <ArrowLeft size={12} /> Back to login
            </button>
            {resetDone ? (
              <div className="text-center py-4">
                <div className="w-12 h-12 bg-emerald-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <CheckCircle2 size={24} className="text-emerald-600" />
                </div>
                <h2 className="text-2xl font-serif font-normal text-[#2A3324] mb-2">Password Reset!</h2>
                <p className="text-sm font-sans text-[#2A3324]/40 mb-6">You can now log in with your new password.</p>
                <button onClick={resetAll} className="bg-[#5C715E] text-[#F7F5F0] rounded-xl px-8 py-3 text-sm font-sans font-medium hover:bg-[#4A5A4B]">
                  Back to Login
                </button>
              </div>
            ) : (
              <>
                <h2 className="text-2xl font-serif font-normal text-[#2A3324] mb-1">Reset Password</h2>
                <p className="text-sm font-sans text-[#2A3324]/40 mb-5">We'll send a verification code to the phone on your account</p>
                {error && <div className="bg-red-50 text-red-600 text-sm p-3 rounded-lg mb-4">{error}</div>}

                {!forgotCodeSent ? (
                  <div className="space-y-4">
                    <div>
                      <label className="text-xs font-sans font-medium uppercase tracking-wider text-[#2A3324]/50 mb-1 block">Email</label>
                      <input data-testid="forgot-email" type="email" value={forgotEmail} onChange={(e) => setForgotEmail(e.target.value)}
                        className="w-full px-4 py-3 rounded-xl border border-[#2A3324]/10 bg-white text-[#2A3324] text-sm focus:outline-none focus:ring-2 focus:ring-[#5C715E]/30" placeholder="your@email.com" />
                    </div>
                    <button data-testid="forgot-send-btn" onClick={handleForgotSendCode} disabled={forgotLoading}
                      className="w-full bg-[#C47C5D] text-[#F7F5F0] rounded-xl py-3 text-sm font-sans font-medium hover:bg-[#B36B4C] transition-all disabled:opacity-50 flex items-center justify-center gap-2">
                      {forgotLoading && <Loader2 size={16} className="animate-spin" />}
                      {forgotLoading ? "Sending..." : "Send Reset Code"}
                    </button>
                  </div>
                ) : (
                  <div className="space-y-4">
                    <div className="bg-emerald-50 border border-emerald-200 rounded-xl p-3">
                      <p className="text-xs font-sans text-emerald-700">Code sent to your phone</p>
                    </div>
                    <div>
                      <label className="text-xs font-sans font-medium uppercase tracking-wider text-[#2A3324]/50 mb-1 block">Verification Code</label>
                      <input data-testid="forgot-code" type="text" value={forgotCode} onChange={(e) => setForgotCode(e.target.value)} maxLength={6}
                        className="w-full px-4 py-3 rounded-xl border border-[#2A3324]/10 bg-white text-[#2A3324] text-lg font-mono tracking-widest text-center focus:outline-none focus:ring-2 focus:ring-[#5C715E]/30" placeholder="000000" autoFocus />
                    </div>
                    <div>
                      <label className="text-xs font-sans font-medium uppercase tracking-wider text-[#2A3324]/50 mb-1 block">New Password</label>
                      <input data-testid="forgot-new-password" type="password" value={newPassword} onChange={(e) => setNewPassword(e.target.value)}
                        className="w-full px-4 py-3 rounded-xl border border-[#2A3324]/10 bg-white text-[#2A3324] text-sm focus:outline-none focus:ring-2 focus:ring-[#5C715E]/30" placeholder="Enter new password" />
                    </div>
                    <button data-testid="forgot-reset-btn" onClick={handleResetPassword} disabled={forgotLoading}
                      className="w-full bg-[#5C715E] text-[#F7F5F0] rounded-xl py-3 text-sm font-sans font-medium hover:bg-[#4A5A4B] transition-all disabled:opacity-50 flex items-center justify-center gap-2">
                      {forgotLoading && <Loader2 size={16} className="animate-spin" />}
                      {forgotLoading ? "Resetting..." : "Reset Password"}
                    </button>
                    <button type="button" onClick={() => { setForgotCodeSent(false); setForgotCode(""); setForgotCodeHint(""); setError(""); }}
                      className="w-full text-xs font-sans text-[#2A3324]/40 hover:text-[#2A3324] transition-colors">
                      Resend code
                    </button>
                  </div>
                )}
              </>
            )}
          </div>
        )}

        {/* ─── ACCOUNT SETUP ─── */}
        {showSetup && !showQuiz && (
          <form onSubmit={handleSetupAccount} data-testid="patient-setup-form" className="bg-[#F7F5F0] rounded-2xl p-8 shadow-2xl">
            <div className="w-12 h-12 bg-[#5C715E]/10 rounded-full flex items-center justify-center mx-auto mb-4">
              <CheckCircle2 size={24} className="text-[#5C715E]" />
            </div>
            <h2 className="text-2xl font-serif font-normal text-[#2A3324] text-center mb-1">Set Up Your Account</h2>
            <p className="text-sm font-sans text-[#2A3324]/40 text-center mb-6">We found your application! Create a password to access your portal.</p>
            {setupError && <div className="bg-red-50 text-red-600 text-sm p-3 rounded-lg mb-4">{setupError}</div>}
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="text-xs font-sans font-medium uppercase tracking-wider text-[#2A3324]/50 mb-1 block">First Name</label>
                  <input data-testid="setup-first-name" type="text" value={setupFirst} onChange={(e) => setSetupFirst(e.target.value)} className="w-full px-4 py-3 rounded-xl border border-[#2A3324]/10 bg-white text-[#2A3324] text-sm focus:outline-none focus:ring-2 focus:ring-[#5C715E]/30" placeholder="First" required />
                </div>
                <div>
                  <label className="text-xs font-sans font-medium uppercase tracking-wider text-[#2A3324]/50 mb-1 block">Last Name</label>
                  <input data-testid="setup-last-name" type="text" value={setupLast} onChange={(e) => setSetupLast(e.target.value)} className="w-full px-4 py-3 rounded-xl border border-[#2A3324]/10 bg-white text-[#2A3324] text-sm focus:outline-none focus:ring-2 focus:ring-[#5C715E]/30" placeholder="Last" required />
                </div>
              </div>
              <div>
                <label className="text-xs font-sans font-medium uppercase tracking-wider text-[#2A3324]/50 mb-1 block">Email</label>
                <input type="email" value={setupEmail} disabled className="w-full px-4 py-3 rounded-xl border border-[#2A3324]/10 bg-[#EFECE5] text-[#2A3324]/60 text-sm" />
              </div>
              <div>
                <label className="text-xs font-sans font-medium uppercase tracking-wider text-[#2A3324]/50 mb-1 block">Create Password</label>
                <input data-testid="setup-password" type="password" value={setupPassword} onChange={(e) => setSetupPassword(e.target.value)} className="w-full px-4 py-3 rounded-xl border border-[#2A3324]/10 bg-white text-[#2A3324] text-sm focus:outline-none focus:ring-2 focus:ring-[#5C715E]/30" placeholder="Min 6 characters" required />
              </div>
            </div>
            <button data-testid="setup-submit-btn" type="submit" disabled={setupLoading} className="w-full mt-6 bg-[#5C715E] text-[#F7F5F0] rounded-xl py-3 text-sm font-sans font-medium hover:bg-[#4A5A4B] transition-all disabled:opacity-50 flex items-center justify-center gap-2">
              {setupLoading && <Loader2 size={16} className="animate-spin" />}
              {setupLoading ? "Creating..." : "Create Account & Enter Portal"}
            </button>
            <button type="button" onClick={resetAll} className="w-full mt-3 text-sm font-sans text-[#2A3324]/40 hover:text-[#2A3324] transition-colors">Back to login</button>
          </form>
        )}

        {/* ─── INLINE QUIZ ─── */}
        {showQuiz && quizPhase === "questions" && (
          <div data-testid="inline-quiz" className="bg-[#F7F5F0] rounded-2xl p-8 shadow-2xl">
            <h2 className="text-xl font-serif font-normal text-[#2A3324] mb-1">Let's Get You Started</h2>
            <p className="text-xs font-sans text-[#2A3324]/40 mb-5">Quick quiz to build your personalized protocol</p>
            <div className="flex items-center justify-between mb-2">
              <span className="text-[10px] font-sans text-[#2A3324]/30 uppercase tracking-wider">Step {quizStep + 1} of {quizSteps.length}</span>
              <span className="text-[10px] font-sans text-[#5C715E]">{Math.round(quizProgress)}%</span>
            </div>
            <div className="w-full h-1 bg-[#2A3324]/10 rounded-full mb-6 overflow-hidden">
              <div className="h-full bg-[#5C715E] rounded-full transition-all duration-300" style={{ width: `${quizProgress}%` }} />
            </div>
            <h3 className="text-base font-sans font-medium text-[#2A3324] mb-4">{quizSteps[quizStep].question}</h3>
            <div className="space-y-2 mb-6">
              {quizSteps[quizStep].options.map((opt) => {
                const selected = (quizAnswers[quizSteps[quizStep].id] || []).includes(opt.value);
                return (
                  <button key={opt.value} data-testid={`iq-${opt.value}`} onClick={() => toggleQuizAnswer(opt.value)}
                    className={`w-full text-left rounded-xl p-3.5 border text-sm font-sans transition-all ${selected ? "bg-[#2A3324] border-[#2A3324] text-[#F7F5F0]" : "bg-white border-[#2A3324]/10 text-[#2A3324] hover:border-[#2A3324]/25"}`}>
                    <div className="flex items-center justify-between">
                      <span>{opt.label}</span>
                      {selected && <CheckCircle2 size={16} className="text-[#C47C5D]" />}
                    </div>
                  </button>
                );
              })}
            </div>
            <div className="flex justify-between">
              <button data-testid="iq-back" onClick={() => quizStep > 0 ? setQuizStep(quizStep - 1) : resetAll()}
                className="text-sm font-sans text-[#2A3324]/50 hover:text-[#2A3324] flex items-center gap-1 transition-colors">
                <ArrowLeft size={14} /> {quizStep === 0 ? "Back to login" : "Back"}
              </button>
              <button data-testid="iq-next" onClick={quizNext} disabled={!quizCanProceed()}
                className={`rounded-xl px-6 py-2.5 text-sm font-sans font-medium flex items-center gap-1.5 transition-all ${quizCanProceed() ? "bg-[#5C715E] text-[#F7F5F0] hover:bg-[#4A5A4B]" : "bg-[#2A3324]/10 text-[#2A3324]/25 cursor-not-allowed"}`}>
                {quizStep === quizSteps.length - 1 ? "Almost Done" : "Next"} <ArrowRight size={14} />
              </button>
            </div>
          </div>
        )}

        {showQuiz && quizPhase === "contact" && (
          <div data-testid="inline-quiz-contact" className="bg-[#F7F5F0] rounded-2xl p-8 shadow-2xl">
            <div className="w-12 h-12 bg-[#5C715E]/10 rounded-full flex items-center justify-center mx-auto mb-4">
              <CheckCircle2 size={24} className="text-[#5C715E]" />
            </div>
            <h2 className="text-xl font-serif font-normal text-[#2A3324] text-center mb-1">Create Your Account</h2>
            <p className="text-xs font-sans text-[#2A3324]/40 text-center mb-6">Enter your info to see your personalized protocol and access your patient portal</p>
            {setupError && <div className="bg-red-50 text-red-600 text-sm p-3 rounded-lg mb-4">{setupError}</div>}
            <div className="space-y-3">
              <div className="grid grid-cols-2 gap-3">
                <input data-testid="iq-first" type="text" value={quizContact.first_name} onChange={(e) => setQuizContact({...quizContact, first_name: e.target.value})} placeholder="First name *" className="px-4 py-3 rounded-xl border border-[#2A3324]/10 bg-white text-[#2A3324] text-sm focus:outline-none focus:ring-2 focus:ring-[#5C715E]/30" required />
                <input data-testid="iq-last" type="text" value={quizContact.last_name} onChange={(e) => setQuizContact({...quizContact, last_name: e.target.value})} placeholder="Last name *" className="px-4 py-3 rounded-xl border border-[#2A3324]/10 bg-white text-[#2A3324] text-sm focus:outline-none focus:ring-2 focus:ring-[#5C715E]/30" required />
              </div>
              <input data-testid="iq-email" type="email" value={quizContact.email} onChange={(e) => setQuizContact({...quizContact, email: e.target.value})} placeholder="Email *" className="w-full px-4 py-3 rounded-xl border border-[#2A3324]/10 bg-white text-[#2A3324] text-sm focus:outline-none focus:ring-2 focus:ring-[#5C715E]/30" required />
              <input data-testid="iq-phone" type="tel" value={quizContact.phone} onChange={(e) => setQuizContact({...quizContact, phone: formatUSPhone(e.target.value)})} placeholder="Phone *" maxLength={17} className="w-full px-4 py-3 rounded-xl border border-[#2A3324]/10 bg-white text-[#2A3324] text-sm focus:outline-none focus:ring-2 focus:ring-[#5C715E]/30" required />
              <input data-testid="iq-password" type="password" value={quizContact.password} onChange={(e) => setQuizContact({...quizContact, password: e.target.value})} placeholder="Create password (min 6 chars) *" className="w-full px-4 py-3 rounded-xl border border-[#2A3324]/10 bg-white text-[#2A3324] text-sm focus:outline-none focus:ring-2 focus:ring-[#5C715E]/30" required />
            </div>
            <div className="flex justify-between mt-6">
              <button onClick={() => { setQuizPhase("questions"); setQuizStep(quizSteps.length - 1); }} className="text-sm font-sans text-[#2A3324]/50 hover:text-[#2A3324] flex items-center gap-1"><ArrowLeft size={14} /> Back</button>
              <button data-testid="iq-submit" onClick={submitQuizAndRegister} disabled={!quizCanProceed() || quizLoading}
                className={`rounded-xl px-6 py-2.5 text-sm font-sans font-medium flex items-center gap-1.5 transition-all ${quizCanProceed() ? "bg-[#C47C5D] text-[#F7F5F0] hover:bg-[#B36B4C]" : "bg-[#2A3324]/10 text-[#2A3324]/25 cursor-not-allowed"}`}>
                {quizLoading ? <><Loader2 size={14} className="animate-spin" /> Creating...</> : <>Create Account & Enter Portal <ArrowRight size={14} /></>}
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
